import * as yup from 'yup'

import { showReceivingSsiQuestion } from './receivingSsiQuestion'

const schema = yup.object({
  primaryIncludeSsi: yup.boolean().nullable().required('This field is required'),
  spouseIncludeSsi: yup
    .boolean()
    .nullable()
    .when('includeSpouse', {
      is: true,
      then: schema => schema.required('This field is required'),
      otherwise: schema => schema,
    }),
  // A begin age is required to include SSI, but not once SSI has started: the
  // saved begin age is cleared for someone already receiving, and requiring it
  // would leave them unable to save any SSI change at all. The requirement is
  // an object-level test rather than a `when` on `primaryReceivingSsi` because
  // that field's own rule depends on the begin age, which yup rejects as a
  // circular dependency.
  primaryPaymentBeginAge: yup.number().nullable(),
  spousePaymentBeginAge: yup.number().nullable(),
  // Required exactly when the form shows the "Already receiving?" question, so
  // an unanswered question can't be saved as an implicit "no".
  primaryReceivingSsi: yup
    .boolean()
    .nullable()
    .when(
      [
        'primaryIncludeSsi',
        'primaryPaymentBeginAge',
        'initialPrimaryReceivingSsi',
        'primaryCurrentAge',
      ],
      {
        is: (primaryIncludeSsi, beginAge, initialReceivingSsi, ageToday) =>
          !!primaryIncludeSsi &&
          showReceivingSsiQuestion({ beginAge, initialReceivingSsi, ageToday }),
        then: schema => schema.required('This field is required'),
        otherwise: schema => schema,
      }
    ),
  spouseReceivingSsi: yup
    .boolean()
    .nullable()
    .when(
      [
        'spouseIncludeSsi',
        'spousePaymentBeginAge',
        'initialSpouseReceivingSsi',
        'spouseCurrentAge',
      ],
      {
        is: (spouseIncludeSsi, beginAge, initialReceivingSsi, ageToday) =>
          !!spouseIncludeSsi &&
          showReceivingSsiQuestion({ beginAge, initialReceivingSsi, ageToday }),
        then: schema => schema.required('This field is required'),
        otherwise: schema => schema,
      }
    ),
  primaryMonthlyAmountCustom: yup
    .number()
    .nullable()
    .when(['primaryIncludeSsi', 'primaryReceivingSsi', 'primaryUseGcEstimate'], {
      is: (primaryIncludeSsi, primaryReceivingSsi, primaryUseGcEstimate) =>
        primaryIncludeSsi &&
        (primaryReceivingSsi || (!primaryReceivingSsi && primaryUseGcEstimate === false)),
      then: schema =>
        schema
          .min(1, 'Please enter an amount between $1 and $100,000,000')
          .max(100000000, 'Please enter an amount between $1 and $100,000,000')
          .required('Monthly amount is required'),
      otherwise: schema => schema,
    }),
  primaryUseGcEstimate: yup
    .boolean()
    .nullable()
    .when(['primaryIncludeSsi', 'primaryReceivingSsi'], {
      is: (primaryIncludeSsi, primaryReceivingSsi) =>
        primaryIncludeSsi && primaryReceivingSsi === false,
      then: schema => schema.required('This field is required'),
      otherwise: schema => schema,
    }),
  spouseUseGcEstimate: yup
    .boolean()
    .nullable()
    .when(['spouseIncludeSsi', 'spouseReceivingSsi'], {
      is: (spouseIncludeSsi, spouseReceivingSsi) =>
        spouseIncludeSsi && spouseReceivingSsi === false,
      then: schema => schema.required('This field is required'),
      otherwise: schema => schema,
    }),
  spouseMonthlyAmountCustom: yup
    .number()
    .nullable()
    .when(['spouseIncludeSsi', 'spouseReceivingSsi', 'spouseUseGcEstimate'], {
      is: (spouseIncludeSsi, spouseReceivingSsi, spouseUseGcEstimate) =>
        spouseIncludeSsi &&
        (spouseReceivingSsi || (!spouseReceivingSsi && spouseUseGcEstimate === false)),
      then: schema =>
        schema
          .min(1, 'Please enter an amount between $1 and $100,000,000')
          .max(100000000, 'Please enter an amount between $1 and $100,000,000')
          .required('Monthly amount is required'),
      otherwise: schema => schema,
    }),
})

// Object-level begin-age requirement (see primaryPaymentBeginAge above).
const requireBeginAge = (person, includeField, receivingField, ageField) => ({
  name: `${person}PaymentBeginAgeRequired`,
  test(values) {
    if (!values?.[includeField] || values[receivingField] === true) {
      return true
    }
    if (values[ageField] !== null && values[ageField] !== undefined) {
      return true
    }
    return this.createError({
      path: ageField,
      message: 'This field is required',
    })
  },
})

const schemaWithBeginAge = schema
  .test(
    requireBeginAge('primary', 'primaryIncludeSsi', 'primaryReceivingSsi', 'primaryPaymentBeginAge')
  )
  .test(
    requireBeginAge('spouse', 'spouseIncludeSsi', 'spouseReceivingSsi', 'spousePaymentBeginAge')
  )

export { schemaWithBeginAge as schema }
